export const isImageFile = (file) => !!file && (file.type.startsWith("image/") || /\.(png|jpe?g|webp|gif|heic|heif|bmp|avif)$/i.test(file.name));

export const readImage = (url) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error("This image could not be opened. Choose a JPG, PNG, WebP or supported image file."));
  image.src = url;
});

export async function editableBlob(file) {
  if (file.size > 100 * 1024 * 1024) throw new Error("Choose an image smaller than 100 MB.");
  if (/\.(heic|heif)$/i.test(file.name) || /image\/hei[cf]/i.test(file.type)) {
    const { default: heic2any } = await import("heic2any");
    const converted = await heic2any({ blob: file, toType: "image/png" });
    return Array.isArray(converted) ? converted[0] : converted;
  }
  return file;
}

export function frameDimensions(image, aspect, crop, mode, minimum = 1) {
  const area = mode === "crop" && crop ? crop : { width: image.naturalWidth, height: image.naturalHeight };
  let longest = Math.min(4096, Math.max(area.width, area.height));
  let width = aspect >= 1 ? longest : longest * aspect;
  let height = width / aspect;
  if (Math.min(width, height) < minimum) {
    const scale = minimum / Math.min(width, height); width *= scale; height *= scale;
  }
  if (width * height > 16000000 || Math.max(width, height) > 8192) throw new Error("Choose a less extreme frame shape for this image.");
  return { width: Math.max(1, Math.round(width)), height: Math.max(1, Math.round(height)) };
}

export async function exportImage({ image, file, aspect, crop, mode, background, minimum, maxBytes }) {
  if (mode === "crop" && (!crop || crop.width <= 0 || crop.height <= 0)) throw new Error("Wait for the crop preview to finish loading.");
  const dimensions = frameDimensions(image, aspect, crop, mode, minimum);
  const canvas = document.createElement("canvas");
  canvas.width = dimensions.width; canvas.height = dimensions.height;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Image editing is not available in this browser.");
  context.imageSmoothingEnabled = true; context.imageSmoothingQuality = "high";
  if (background !== "transparent") { context.fillStyle = background; context.fillRect(0, 0, canvas.width, canvas.height); }
  if (mode === "crop") {
    context.drawImage(image, crop.x, crop.y, crop.width, crop.height, 0, 0, canvas.width, canvas.height);
  } else {
    const scale = Math.min(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
    const width = image.naturalWidth * scale, height = image.naturalHeight * scale;
    context.drawImage(image, (canvas.width - width) / 2, (canvas.height - height) / 2, width, height);
  }
  // Support fix (ticket 274782): photos (JPEG, no transparency needed) stay high-quality JPEG
  // instead of being converted to a much larger PNG; transparent backgrounds still use PNG.
  const isJpeg = /image\/jpe?g/i.test(file.type) || /\.jpe?g$/i.test(file.name);
  const asJpeg = isJpeg && (mode === "crop" || background !== "transparent");
  const type = asJpeg ? "image/jpeg" : "image/png";
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, type, asJpeg ? 0.95 : undefined));
  if (!blob) throw new Error("The edited image could not be saved.");
  if (blob.size > maxBytes) throw new Error(`This edited image exceeds ${Math.round(maxBytes / 1024 / 1024)} MB. Choose a smaller image or use the original.`);
  return new File([blob], `${file.name.replace(/\.[^.]+$/, "")}-edited.${asJpeg ? "jpg" : "png"}`, { type, lastModified: Date.now() });
}